/**
 * Agent Inbox — the queue the owner clears each morning.
 *
 * CARDS, NOT A TABLE. At a handful of proposals a day, a table costs a second
 * click to reach the only two things that matter: what it says, and who gets
 * it. The layout is facts-left / member-preview-right — the RuleStudio split
 * applied to a queue instead of a form.
 *
 * APPROVE GOES THROUGH AN EDGE FUNCTION, NOT A ROW UPDATE. cm_proposals has no
 * admin UPDATE policy, for the same three reasons class_year_change_requests
 * does not: approving has effects no row write can perform; it must be
 * re-validated at dispatch against settings that may have changed since; and a
 * stray useUpdate should fail loudly rather than mark a row approved while
 * nothing sends.
 *
 * App.useApp().modal throughout — the static Modal does not mount under this
 * app's React 19 setup.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { App, Button, Input, Tooltip } from 'antd'
import { Link } from 'react-router'
import { ListPage, SectionCard, Pill, EmptyHint, type Stat } from '../../../theme/listkit'
import { supabaseClient } from '../../../lib/supabaseClient'
import { useCmSettings } from '../useCmSettings'
import {
  actionLabel, actionsForMode, applyEdits, channelLabel, effectiveCopy, heldByChannel, reachSentence,
  noteRequired, riskLabel, riskTierAfterEdit, riskTone, statusLabel, statusTone,
  type DecisionAction, type EditableField,
} from '../decision'
import { modeEntry } from '../modes'
import { RejectDialog } from './RejectDialog'
import { saveGrade } from '../grading'
import { profileHref } from '../signals'
import { useProfileIds } from '../useProfileIds'
import type { CmPreview, CmProposal } from '../types'

export function CmInboxPage() {
  const [items, setItems] = useState<CmProposal[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<CmProposal | null>(null)
  const [grades, setGrades] = useState<Record<string, 'up' | 'down'>>({})
  const { settings } = useCmSettings()
  const { modal, message } = App.useApp()

  const [previews, setPreviews] = useState<Record<string, CmPreview | null>>({})

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabaseClient
      .from('cm_proposals').select('*')
      .in('status', ['pending_review', 'approved', 'scheduled', 'failed'])
      .order('risk_tier', { ascending: true })
      .order('created_at', { ascending: false })
      .limit(100)
    const rows = (data as CmProposal[]) ?? []
    setItems(rows)
    const { data: g } = await supabaseClient.from('cm_proposal_grades').select('proposal_id, grade')
    setGrades(Object.fromEntries(((g ?? []) as { proposal_id: string; grade: 'up' | 'down' }[])
      .map((x) => [x.proposal_id, x.grade])))
    setLoading(false)
    // Reach and drift come from the SERVER, per card. The browser can count an
    // audience, but only the database knows who the rollout allowlist and the
    // restrictions will actually let through, and whether the fact behind a
    // proposal is still true.
    const entries = await Promise.all(rows.map(async (r) => {
      const { data: pv } = await supabaseClient.rpc('cm_proposal_preview', { p_id: r.id })
      return [r.id, (pv as unknown as CmPreview) ?? null] as const
    }))
    setPreviews(Object.fromEntries(entries))
  }, [])

  useEffect(() => { void load() }, [load])

  // Profile evidence names the member by user_id; the link needs profiles.id.
  const profileIds = useProfileIds(items
    .filter((p) => String(p.evidence?.table ?? '').includes('profiles'))
    .map((p) => p.evidence?.user_id)
    .filter((u): u is string => typeof u === 'string'))

  const mode = settings?.mode ?? 'off'
  const waiting = items.filter((p) => p.status === 'pending_review').length

  const stats: Stat[] = [
    { label: 'Waiting for you', value: String(waiting), tone: waiting > 0 ? 'gold' : 'green' },
    { label: 'Approved, not yet sent', value: String(items.filter((p) => p.status === 'approved' || p.status === 'scheduled').length) },
    { label: 'Failed', value: String(items.filter((p) => p.status === 'failed').length),
      tone: items.some((p) => p.status === 'failed') ? 'red' : 'neutral' },
    { label: 'Mode', value: modeEntry(mode).title, tone: mode === 'off' ? 'neutral' : 'gold' },
  ]

  async function grade(proposalId: string, g: 'up' | 'down') {
    try {
      await saveGrade(proposalId, g)
      setGrades((prev) => ({ ...prev, [proposalId]: g }))
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e))
    }
  }

  async function decide(p: CmProposal, action: DecisionAction, note?: string, edits?: Partial<Record<EditableField, string>>) {
    setBusyId(p.id)
    try {
      const { data, error } = await supabaseClient.functions.invoke('community-decide', {
        body: { proposalId: p.id, action, note: note ?? null, edits: edits ?? null },
      })
      if (error) throw error
      const res = data as { error?: string; dispatch?: { ok?: boolean; error?: string } } | null
      if (res?.error) throw new Error(res.error)
      // Partial success is reported as such, never as a clean win.
      if (res?.dispatch && res.dispatch.ok === false) {
        message.warning(`Saved, but the dispatcher could not be reached: ${res.dispatch.error ?? 'unknown'}. The next scheduled run will pick it up.`)
      } else {
        message.success(`${actionLabel(action)} — done.`)
      }
      await load()
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  function onAction(p: CmProposal, action: DecisionAction) {
    if (action === 'approve_edited') { openEditor(p); return }

    if (noteRequired(action)) {
      setRejecting(p)
      return
    }

    modal.confirm({
      title: `${actionLabel(action)}?`,
      content: (
        <div style={{ fontSize: 13 }}>
          <p style={{ marginTop: 0 }}>
            {action === 'cancel'
              ? 'This proposal is queued but has not gone out. Recalling it stops that.'
              : reachSentence(p, previews[p.id])}
          </p>
          {action !== 'cancel' && (
            <p style={{ marginBottom: 0, opacity: 0.75 }}>
              The dispatcher re-checks the kill switch, the mode, quiet hours and the caps before anything is sent.
            </p>
          )}
        </div>
      ),
      okText: actionLabel(action),
      onOk: () => decide(p, action),
    })
  }

  function openEditor(p: CmProposal) {
    const original = { title: p.title, body: p.body, feed_text: p.feed_text } as Record<string, unknown>
    // Controlled state, deliberately NOT an antd Form: rc-field-form submits
    // only registered name paths, which is how RuleFormFields silently deleted
    // unbound jsonb keys on every save. Here the wire carries a patch of named
    // fields, so a field the UI does not know about cannot be dropped.
    const edits: Partial<Record<EditableField, string>> = {}
    const current = effectiveCopy(p)

    modal.confirm({
      title: 'Edit, then approve',
      width: 620,
      content: (
        <div>
          <label style={{ fontSize: 12, opacity: 0.7 }}>Title</label>
          <Input
            data-testid="cm-edit-title"
            defaultValue={current.title}
            onChange={(e) => { edits.title = e.target.value }}
          />
          <label style={{ fontSize: 12, opacity: 0.7, marginTop: 8, display: 'block' }}>Body</label>
          <Input.TextArea
            rows={4}
            data-testid="cm-edit-body"
            defaultValue={current.body}
            onChange={(e) => { edits.body = e.target.value }}
          />
          <p style={{ marginTop: 10, marginBottom: 0, fontSize: 12, opacity: 0.75 }}>
            Rewriting this makes it your words rather than a template, which means it no longer qualifies to send
            itself in Tiered mode — it will always wait for you.
          </p>
        </div>
      ),
      okText: 'Save & approve',
      onOk: () => {
        const nextTier = riskTierAfterEdit(p.risk_tier, original, edits)
        if (nextTier > p.risk_tier) {
          // Not a blocker, just honesty about what the edit did.
          message.info('Saved as your wording — this one will always wait for you.')
        }
        // applyEdits is what guarantees unknown keys survive; the patch below
        // is what guarantees they are never transmitted in the first place.
        void applyEdits(original, edits)
        return decide(p, 'approve_edited', undefined, edits)
      },
    })
  }

  return (
    <ListPage
      intro={{
        eyebrow: 'Messaging · Community Manager',
        title: 'Inbox',
        description: 'What the community manager wants to do, why it thinks so, and exactly how it will look.',
        actions: <Button onClick={() => void load()} loading={loading}>Refresh</Button>,
      }}
      stats={stats}
      statColumns={4}
    >
      <RejectDialog
        open={rejecting !== null}
        onSubmit={(note) => (rejecting ? decide(rejecting, 'reject', note) : Promise.resolve())}
        onClose={() => setRejecting(null)}
      />

      {/* The same card means different things in each mode; say so once, at the top. */}
      <div
        data-testid="cm-mode-banner"
        style={{
          maxWidth: 1100, marginBottom: 12, padding: '10px 14px', borderRadius: 10,
          background: mode === 'shadow' ? 'rgba(200,160,40,.10)' : 'rgba(0,0,0,.035)',
          fontSize: 13,
        }}
      >
        <strong>{modeEntry(mode).title}.</strong>{' '}
        {mode === 'shadow'
          ? 'Nothing below has been sent, or can be. These are the choices the agent would have made — mark them on the Runs page.'
          : mode === 'off'
            ? 'The agent is not running. Anything below is left over and will expire on its own.'
            : modeEntry(mode).what}{' '}
        <Link to="/agent/controls">Change this</Link>
      </div>

      {items.length === 0 && !loading && (
        <SectionCard title="Nothing waiting" style={{ maxWidth: 1100 }}>
          <EmptyHint>
            The queue is empty. That is a legitimate result — the agent is supposed to be quiet on a day when nothing
            has gone unanswered.
          </EmptyHint>
        </SectionCard>
      )}

      {items.map((p) => {
        const copy = effectiveCopy(p)
        const actions = actionsForMode(p.status, mode)
        const count = recipientCount(p)
        const evidenceHref = evidenceLink(p, profileIds)
        return (
          <SectionCard key={p.id} title={copy.title || p.kind} style={{ maxWidth: 1100 }}>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {/* facts */}
              <div style={{ flex: '1 1 460px', minWidth: 300 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  <Pill tone={statusTone(p.status)}>{statusLabel(p.status)}</Pill>
                  <Pill tone={riskTone(p.risk_tier)}>{riskLabel(p.risk_tier)}</Pill>
                  {p.channels.map((c) => <Pill key={c} tone="neutral">{channelLabel(c)}</Pill>)}
                  {heldByChannel(p, settings) && <Pill tone="gold">held — channel off</Pill>}
                  {p.edited_payload && <Pill tone="jaguar">your wording</Pill>}
                  {p.kind === 'admin_task' && <Pill tone="jaguar">for you, not a member</Pill>}
                </div>

                {heldByChannel(p, settings) && (
                  <div style={{ fontSize: 13, marginBottom: 6 }}>
                    Waiting because {p.channels.map(channelLabel).join(' and ')}{' '}
                    {p.channels.length === 1 ? 'is' : 'are'} switched off. It goes out on the next run after you{' '}
                    <Link to="/agent/controls">turn {p.channels.length === 1 ? 'it' : 'one'} back on</Link>, or you can recall it.
                  </div>
                )}
                <div style={{ fontSize: 13, marginBottom: 6 }} data-testid={`cm-reach-${p.id}`}>
                  <strong>Reaches:</strong>{' '}
                  {reachLine(p, previews[p.id], count)}
                </div>
                {previews[p.id]?.still_true === false && (
                  <div
                    data-testid={`cm-drift-${p.id}`}
                    style={{
                      margin: '6px 0 8px', padding: '8px 10px', borderRadius: 8, fontSize: 13,
                      background: 'rgba(200,160,40,.12)', border: '1px solid rgba(200,160,40,.35)',
                    }}
                  >
                    <strong>This may no longer be needed.</strong> {previews[p.id]?.drift}
                  </div>
                )}

                {p.rationale && (
                  <blockquote style={{ margin: '8px 0', paddingLeft: 10, borderLeft: '3px solid rgba(0,0,0,.12)', fontSize: 13 }}>
                    {p.rationale}
                  </blockquote>
                )}

                {Object.keys(p.evidence ?? {}).length > 0 && (
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    <strong>Evidence:</strong>{' '}
                    {Object.entries(p.evidence).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
                    {evidenceHref && <> · <Link to={evidenceHref}>open it</Link></>}
                  </div>
                )}

                <div style={{ fontSize: 11, opacity: 0.55, marginTop: 8, fontFamily: 'monospace' }}>
                  {p.dedup_key}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {p.scheduled_for ? `Scheduled ${new Date(p.scheduled_for).toLocaleString()}` : 'Sends on the next run'}
                  {' · '}expires {new Date(p.expires_at).toLocaleDateString()}
                  {p.origin === 'playbook' ? ' · from a rule' : p.origin === 'brain' ? ' · written by the agent' : ''}
                </div>

                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {mode === 'shadow' && (
                    <>
                      <span style={{ fontSize: 12, opacity: 0.7, alignSelf: 'center' }}>Would this have been right?</span>
                      <Button
                        size="small"
                        data-testid={`cm-grade-up-${p.id}`}
                        type={grades[p.id] === 'up' ? 'primary' : 'default'}
                        onClick={() => void grade(p.id, 'up')}
                      >Yes, good</Button>
                      <Button
                        size="small"
                        data-testid={`cm-grade-down-${p.id}`}
                        danger={grades[p.id] === 'down'}
                        onClick={() => void grade(p.id, 'down')}
                      >No, not good</Button>
                    </>
                  )}
                  {actions.length === 0 && mode !== 'shadow' && (
                    <span style={{ fontSize: 12, opacity: 0.6 }}>Nothing to do here.</span>
                  )}
                  {actions.map((a) => (
                    <Button
                      key={a}
                      size="small"
                      data-testid={`cm-action-${a}-${p.id}`}
                      type={a === 'approve' ? 'primary' : 'default'}
                      danger={a === 'reject'}
                      loading={busyId === p.id}
                      onClick={() => onAction(p, a)}
                    >{actionLabel(a)}</Button>
                  ))}
                </div>
              </div>

              {/* preview */}
              <div style={{ flex: '0 1 320px', minWidth: 260 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.6, marginBottom: 6 }}>
                  How it will look
                </div>
                <div style={{ border: '1px solid rgba(0,0,0,.10)', borderRadius: 10, padding: 12, background: '#fff' }}>
                  {p.channels.includes('feed') ? (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>MUAA Alumni</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}>{copy.feedText || copy.body}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{copy.title}</div>
                      <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>{copy.body}</div>
                      <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>{p.grp} · now</div>
                    </>
                  )}
                </div>
                {p.channels.includes('push') && (
                  <div style={{ fontSize: 11, opacity: 0.65, marginTop: 6 }}>
                    Push has never been confirmed to arrive in this app — a "sent" count is not proof of delivery.
                  </div>
                )}
              </div>
            </div>
          </SectionCard>
        )
      })}
    </ListPage>
  )
}

/** null means "everyone" — an unbounded audience is worth showing differently. */
function recipientCount(p: CmProposal): number | null {
  const t = p.audience?.type
  if (t === 'all_members') return null
  if (t === 'generic') return 0
  return p.audience?.userIds?.length ?? 0
}

function evidenceLink(p: CmProposal, profileIds: ReadonlyMap<string, string>): string | null {
  const t = String(p.evidence?.table ?? '')
  if (t.includes('donations')) return '/donations'
  if (t.includes('mentorship')) return '/mentorship_requests'
  if (t.includes('rsvps')) return '/rsvps'
  if (t.includes('profiles')) return profileHref(p.evidence?.user_id, profileIds)
  return null
}

/**
 * The reach sentence, from the server's numbers when they have arrived.
 * Says plainly when the rollout allowlist or a restriction means a proposal
 * will reach fewer people than it names — including nobody.
 */
function reachLine(p: CmProposal, pv: CmPreview | null | undefined, clientCount: number | null): string {
  if (p.kind === 'admin_task') return 'you and the other admins'
  if (p.audience?.type === 'generic') return 'the community feed'
  if (!pv) return clientCount === null ? 'everyone' : `${clientCount} member${clientCount === 1 ? '' : 's'}`
  const named = pv.audience_count
  const real = pv.reachable_count
  const who = pv.first_recipient_first_name && named === 1 ? ` (${pv.first_recipient_first_name})` : ''
  if (real === named) return `${real} member${real === 1 ? '' : 's'}${who}`
  const why = [
    pv.outside_rollout ? `${pv.outside_rollout} not on the rollout allowlist` : '',
    pv.restricted ? `${pv.restricted} restricted` : '',
  ].filter(Boolean).join(', ')
  return `${real} of ${named}${who} — ${why}`
}
